import {Flag, Tooltip} from 'platform/components';
import {Box, Center, Hide, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatPercentage} from 'platform/locale';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda-adjunct';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {CompareTrend} from '../../../components/CompareTrend/CompareTrend';
import {CatalogueType} from '../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../i18n/i18n';
import {useGetTrimLevelsQuery} from '../../../store/catalogueApi';
import {AvailableCar} from '../../../types/AvailableCar';
import {PriceReportType} from '../../../types/PriceReportType';
import {getDaysOnStock} from '../../../utils/getDaysOnStock';
import {getDifference} from '../../../utils/getDifference';
import {getPrice} from '../../../utils/getPrice';
import {mapMetadaCurrency} from '../../../utils/mapMetadaCurrency';
import {mileageFormatter} from '../../../utils/mileageFormatter';
import {powerFormatter} from '../../../utils/powerFormatter';
import {DEFAULT_VALUE} from '../consts/defaultValue';
import {FeatureList} from './FeatureList';
import {PriceChangesPopper} from './PriceChangesPopper';
import {VerticalValue} from './VerticalValue';

interface ColumnHeaderProps extends TestIdProps {
  priceReport: PriceReportType;
  catalogue: CatalogueType;
  vehicle: AvailableCar;
  vehiclePosition: number | Nullish;
  isMyVehicle: boolean;
  isMyAd: boolean;
  hasAnyVehicleDiscount: boolean;
}

export function ColumnHeader(props: ColumnHeaderProps) {
  const formatPercentage = useFormatPercentage();
  const formatCurrency = useFormatCurrency();

  const {getTransmission, getSellerType, getFuelType, getDrive, getCarStyle} = props.catalogue;

  const daysOnStock = getDaysOnStock(props.vehicle, props.priceReport);
  const vehicleSellerValues = props.isMyVehicle
    ? [EMPTY_PLACEHOLDER, EMPTY_PLACEHOLDER]
    : [
        isNotNil(daysOnStock) ? daysOnStock.toString() : DEFAULT_VALUE,
        isNotNil(props.vehicle.seller?.type)
          ? getSellerType(props.vehicle.seller?.type)
          : DEFAULT_VALUE,
      ];

  const vehicleDetailValues = [
    getTransmission(props.vehicle.transmission),
    getFuelType(props.vehicle.fuel_type),
    powerFormatter(props.vehicle.maxPower ?? props.vehicle.power),
    getDrive(props.vehicle.drive_type),
    getCarStyle(props.vehicle.car_style),
  ];

  const {data: vehicleTranslatedInfo} = useVehicleTranslatedInfo(
    isNotNil(props.vehicle.model)
      ? {...props.priceReport.vehicle, modelFamily: props.vehicle.model}
      : props.priceReport.vehicle
  );
  const {data: trimLevels} = useGetTrimLevelsQuery(props.priceReport.vehicle, {
    skip: props.isMyVehicle,
  });
  const vehicleTrimLevel = props.isMyVehicle
    ? (vehicleTranslatedInfo.trimLevel ?? EMPTY_PLACEHOLDER)
    : (trimLevels?.trim_levels?.find(
        (trimLevel) => trimLevel.const_key === props.vehicle.trim_level
      )?.label ?? EMPTY_PLACEHOLDER);
  const vehicleName = match([
    props.vehicle.ad_title,
    props.isMyVehicle,
    props.priceReport.vehicle.vehicleTitle,
  ])
    .with([Pattern.nonNullable, false, Pattern.any], () => props.vehicle.ad_title)
    .with([Pattern.any, true, Pattern.nonNullable], () => props.priceReport.vehicle.vehicleTitle)
    .otherwise(() => `${vehicleTranslatedInfo.make} ${vehicleTranslatedInfo.model}`);
  const vehiclePrice = getPrice(props.vehicle, props.priceReport);
  const priceDifference = getDifference(
    vehiclePrice,
    props.priceReport.sellingPrice.recommended.value
  );
  const currency = props.priceReport.sellingPrice.recommended.currency;
  const firstScrapedPrice = props.vehicle.first_scraped_price_with_vat ?? vehiclePrice;
  const firstScrapedPriceCurrency =
    mapMetadaCurrency(props.vehicle.first_scraped_price_currency) ?? currency;
  const shouldShowFirstScrapedPrice = firstScrapedPrice !== vehiclePrice;
  const yearDifference = getDifference(
    props.vehicle.year,
    props.priceReport.vehicle.firstRegistrationOnYear
  );
  const mileageDifference = getDifference(props.vehicle.mileage, props.priceReport.vehicle.mileage);
  const featuresDifference = getDifference(
    props.vehicle.feature_score,
    props.priceReport.featuresLevel,
    100
  );

  return (
    <VStack>
      <Box padding={2} borderBottom="1px solid" borderColor="general.separator" height={16}>
        <ThreeLinesTruncate>
          <Text title={vehicleName ?? undefined} size="xSmall" overflowWrap="anywhere">
            {vehicleName}
          </Text>
        </ThreeLinesTruncate>
      </Box>
      <Box
        padding={2}
        borderBottom="1px solid"
        borderColor="general.separator"
        height={props.hasAnyVehicleDiscount ? 18 : 13}
      >
        <VStack spacing={1}>
          <Show when={shouldShowFirstScrapedPrice}>
            <HStack spacing={1} align="center">
              <Text size="xxSmall" color="secondary" strikethrough>
                {formatCurrency(firstScrapedPrice, firstScrapedPriceCurrency)}
              </Text>
              <PriceChangesPopper
                vehicle={props.vehicle}
                priceReport={props.priceReport}
                popperPlacement="top-start"
              />
            </HStack>
          </Show>
          <Hide when={shouldShowFirstScrapedPrice || !props.hasAnyVehicleDiscount}>
            <Space vertical={4} />
          </Hide>
          <HStack spacing={1} align="center">
            <Text size="xSmall" alternative>
              {formatCurrency(vehiclePrice, currency)}
            </Text>
            <Show when={props.vehicle.vat_reclaimable}>
              <Tooltip label={i18n.t('general.labels.vatDeductible')}>
                <Flag
                  size="small"
                  colorScheme="black"
                  label={i18n.t('general.labels.vat')}
                  data-testid={suffixTestId('vatDeductible', props)}
                />
              </Tooltip>
            </Show>
          </HStack>
          <Hide when={props.isMyVehicle}>
            <CompareTrend
              value={priceDifference}
              formatter={(value) => formatCurrency(value, currency) ?? ''}
              reverse
            />
          </Hide>
        </VStack>
      </Box>
      <Box padding={2} borderBottom="1px solid" borderColor="general.separator" height={7}>
        <Center height="100%" justify="flex-start">
          <Text size="xSmall">{`${props.vehiclePosition}.`}</Text>
        </Center>
      </Box>
      <Box padding={2} borderBottom="1px solid" borderColor="general.separator" height={18}>
        <Box height={10}>
          <VerticalValue
            value={props.vehicle.feature_score}
            difference={props.isMyVehicle ? undefined : featuresDifference}
            formatter={(value) =>
              isNotNil(value) ? formatPercentage(value, 'down') : DEFAULT_VALUE
            }
            differenceFormatter={(value) =>
              // Feature difference is already rounded – formatting has to be the nearest due to rounding problems (0.29 * 100 = 28.999999999999996)
              isNotNil(value) ? formatPercentage(value, 'nearest') : DEFAULT_VALUE
            }
            withRank
            isMyVehicle={props.isMyVehicle}
            data-testid={suffixTestId('featureScore', props)}
          />
        </Box>
        <Text size="xSmall">{vehicleTrimLevel}</Text>
      </Box>
      <Box padding={2} borderBottom="1px solid" borderColor="general.separator" height={14}>
        <VerticalValue
          value={props.vehicle.mileage}
          difference={props.isMyVehicle ? undefined : mileageDifference}
          formatter={mileageFormatter}
          differenceReverseColor
          isMyVehicle={props.isMyVehicle}
          data-testid={suffixTestId('mileage', props)}
        />
      </Box>
      <Box padding={2} borderBottom="1px solid" borderColor="general.separator" height={14}>
        <VerticalValue
          value={props.vehicle.year}
          difference={props.isMyVehicle ? undefined : yearDifference}
          isMyVehicle={props.isMyVehicle}
          data-testid={suffixTestId('year', props)}
        />
      </Box>
      <Box borderBottom="1px solid" borderColor="general.separator">
        <FeatureList
          spacing={1}
          textColor="primary"
          values={vehicleSellerValues}
          isMyVehicle={props.isMyVehicle}
          isMyAd={props.isMyAd}
        />
      </Box>
      <Box borderBottom="1px solid" borderColor="general.separator">
        <FeatureList
          spacing={1}
          textColor="primary"
          values={vehicleDetailValues}
          isMyVehicle={props.isMyVehicle}
          isMyAd={props.isMyAd}
          data-testid={suffixTestId('details', props)}
        />
      </Box>
    </VStack>
  );
}

const ThreeLinesTruncate = styled.div`
  /* This property doesn't exists without -webkit prefix, but it is compatible even on browsers not based on webkit */
  /* stylelint-disable */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
